import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Play, ArrowRight, Truck, Server, Clock, Wifi, Shield, Check, Mail, HardDrive } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AuthModal from '../components/AuthModal';
import SearchModal from '../components/SearchModal';

const HERO_IMG = "https://images.unsplash.com/photo-1595769816263-9b910be24d5f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwyfHxjaW5lbWElMjB0aGVhdGVyJTIwZGFyayUyMGF1ZGl0b3JpdW0lMjBsdXh1cnl8ZW58MHx8fHwxNzc5ODgxNjM2fDA&ixlib=rb-4.1.0&q=85";
const SERVER_IMG = "https://images.pexels.com/photos/37730212/pexels-photo-37730212.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const PROJECTION_IMG = "https://images.unsplash.com/photo-1759230766134-e3ff1c27d20e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxjaW5lbWElMjBwcm9qZWN0aW9uJTIwcm9vbSUyMHRlY2hub2xvZ3klMjBzZXJ2ZXJ8ZW58MHx8fHwxNzc5ODgxNjM2fDA&ixlib=rb-4.1.0&q=85";
const BRAVO_DEVICE = "https://customer-assets.emergentagent.com/job_07253317-1e1c-471e-bbd1-023abce1b255/artifacts/1bivw15k_imagen_bravo_01.jpg";
const CINEMA_DARK = "https://images.pexels.com/photos/29505262/pexels-photo-29505262.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

const useScrollFadeIn = (threshold = 0.15) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsVisible(true); observer.unobserve(entry.target); } },
      { threshold }
    );
    const el = ref.current;
    if (el) observer.observe(el);
    return () => { if (el) observer.unobserve(el); };
  }, [threshold]);
  return [ref, isVisible];
};

const AnimatedSection = ({ children, className = '', delay = 0 }) => {
  const [ref, isVisible] = useScrollFadeIn();
  return (
    <div ref={ref} className={`transition-all duration-1000 ease-out ${className}`}
      style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(40px)', transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
};

const DistribucionDCPPage = () => {
  const [showAuth, setShowAuth] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Header onOpenAuth={() => setShowAuth(true)} onOpenSearch={() => setShowSearch(true)} />

      {/* =============== HERO =============== */}
      <section className="relative min-h-screen flex items-end" data-testid="dcp-hero">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Sala de cine" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/70 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a]/80 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32 pt-32">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-[#e4002b] rounded flex items-center justify-center">
                <Play className="w-6 h-6 text-white fill-white" />
              </div>
              <span className="text-[#e4002b] font-bold text-lg tracking-widest uppercase">Distribución DCP</span>
            </div>

            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-none mb-6" style={{ fontFamily: '"Oswald", sans-serif' }}>
              MUCHO MÁS QUE UN REPRODUCTOR,
              <span className="block text-[#e4002b]">TU ALIADO EN LA GESTIÓN</span>
            </h1>

            <p className="text-xl md:text-2xl text-white/80 font-light mb-4">
              Bravo ha evolucionado para transformar la forma en que los cines reciben y gestionan sus películas y eventos.
            </p>
            <p className="text-lg text-white/60 leading-relaxed mb-10 max-w-xl">
              Lo que comenzó como un excelente reproductor de contenido alternativo, es hoy una herramienta integral
              que optimiza tu flujo de trabajo, ahorrándote tiempo, costes y complicaciones logísticas.
            </p>

            <div className="flex flex-wrap gap-4">
              <a href="mailto:m.cazorla@versiondigital.es?subject=Solicitud Demo Bravo DCP"
                className="inline-flex items-center gap-3 bg-[#e4002b] text-white px-8 py-4 font-bold tracking-wider uppercase hover:bg-[#c70024] transition-all hover:scale-105 shadow-[0_0_30px_rgba(228,0,43,0.4)]"
                data-testid="dcp-hero-demo-btn">
                Solicitar Demo <ArrowRight className="w-5 h-5" />
              </a>
              <a href="mailto:m.cazorla@versiondigital.es?subject=Contratar Bravo DCP"
                className="inline-flex items-center gap-3 border-2 border-white text-white px-8 py-4 font-bold tracking-wider uppercase hover:bg-white hover:text-[#0a0a0a] transition-all"
                data-testid="dcp-hero-contract-btn">
                Contratar <Mail className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-white/50 rounded-full" />
          </div>
        </div>
      </section>

      {/* =============== WHY BRAVO =============== */}
      <section className="py-24 md:py-32 bg-[#0a0a0a] relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(228,0,43,0.08)_0%,transparent_60%)]" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <AnimatedSection className="text-center mb-16">
            <p className="text-[#e4002b] text-sm font-bold tracking-widest uppercase mb-4">Ventajas</p>
            <h2 className="text-3xl md:text-5xl font-bold text-white" style={{ fontFamily: '"Oswald", sans-serif' }}>
              ¿POR QUÉ BRAVO CAMBIARÁ TU DÍA A DÍA?
            </h2>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Card 1 - Large */}
            <AnimatedSection className="md:col-span-2 md:row-span-2" delay={100}>
              <div className="h-full bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border border-white/10 group hover:border-[#e4002b]/50 transition-all duration-500 relative overflow-hidden">
                <div className="absolute inset-0">
                  <img src={PROJECTION_IMG} alt="Sala de cine" className="w-full h-full object-cover opacity-30 group-hover:opacity-40 transition-opacity duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/80 to-transparent" />
                </div>
                <div className="relative z-10 p-8 md:p-12 flex flex-col justify-end h-full min-h-[400px]">
                  <div className="w-16 h-16 bg-[#e4002b]/20 backdrop-blur-sm rounded-xl flex items-center justify-center mb-6 group-hover:bg-[#e4002b]/30 transition-colors">
                    <Truck className="w-8 h-8 text-[#e4002b]" />
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-4" style={{ fontFamily: '"Oswald", sans-serif' }}>
                    ADIÓS A LOS ENVÍOS FÍSICOS
                  </h3>
                  <p className="text-white/70 text-lg leading-relaxed max-w-lg">
                    Olvídate del estrés que suponen los discos duros, los retrasos en el transporte, los extravíos
                    o la compleja gestión logística. Bravo recibe los DCPs de forma <strong className="text-white">100% remota y digital</strong>,
                    directamente en el equipo.
                  </p>
                </div>
              </div>
            </AnimatedSection>

            {/* Card 2 */}
            <AnimatedSection delay={200}>
              <div className="h-full bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border border-white/10 p-8 group hover:border-[#e4002b]/50 transition-all duration-500">
                <div className="w-14 h-14 bg-[#e4002b]/20 rounded-xl flex items-center justify-center mb-5 group-hover:bg-[#e4002b]/30 transition-colors">
                  <Server className="w-7 h-7 text-[#e4002b]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3" style={{ fontFamily: '"Oswald", sans-serif' }}>
                  INGESTA AUTOMÁTICA Y SIN ERRORES
                </h3>
                <p className="text-white/60 leading-relaxed">
                  Una vez que el contenido llega a Bravo, se almacena de forma segura. Al conectarlo a tu red,
                  puedes volcar las películas directamente al servidor central o a la librería.
                  <strong className="text-white/80"> Sin pasos manuales, sin pérdidas de tiempo y con total fiabilidad.</strong>
                </p>
              </div>
            </AnimatedSection>

            {/* Card 3 */}
            <AnimatedSection delay={300}>
              <div className="h-full bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border border-white/10 p-8 group hover:border-[#e4002b]/50 transition-all duration-500">
                <div className="w-14 h-14 bg-[#e4002b]/20 rounded-xl flex items-center justify-center mb-5 group-hover:bg-[#e4002b]/30 transition-colors">
                  <Clock className="w-7 h-7 text-[#e4002b]" />
                </div>
                <h3 className="text-xl font-bold text-white mb-3" style={{ fontFamily: '"Oswald", sans-serif' }}>
                  MAYOR MARGEN DE ORGANIZACIÓN
                </h3>
                <p className="text-white/60 leading-relaxed">
                  Gana tranquilidad organizando tu cartelera con antelación. <strong className="text-white/80">Almacena el contenido previamente</strong> y
                  muévelo exactamente cuando lo necesites, optimizando tus tiempos operativos.
                </p>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* =============== INTEGRATION =============== */}
      <section className="py-24 md:py-32 bg-[#0a0a0a] relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_50%,rgba(228,0,43,0.06)_0%,transparent_60%)]" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Image column */}
            <AnimatedSection>
              <div className="relative">
                <div className="aspect-[4/3] rounded-lg overflow-hidden border border-white/10">
                  <img src={SERVER_IMG} alt="Servidor de cine" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a]/60 to-transparent" />
                </div>
                {/* Floating device image */}
                <div className="absolute -bottom-6 -right-6 w-40 h-40 rounded-lg overflow-hidden border-2 border-[#e4002b]/50 shadow-2xl shadow-[#e4002b]/20">
                  <img src={BRAVO_DEVICE} alt="Dispositivo Bravo" className="w-full h-full object-cover" />
                </div>
              </div>
            </AnimatedSection>

            {/* Text column */}
            <AnimatedSection delay={200}>
              <p className="text-[#e4002b] text-sm font-bold tracking-widest uppercase mb-4">Compatibilidad</p>
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: '"Oswald", sans-serif' }}>
                INTEGRACIÓN TOTAL Y SIN COMPLICACIONES
              </h2>
              <p className="text-lg text-white/70 leading-relaxed mb-8">
                No importa qué tecnología utilices en tus cabinas. Bravo está diseñado para entenderse a la perfección
                y de forma nativa con los principales servidores del mercado a través de una
                <strong className="text-white"> conexión FTP estándar, rápida y segura:</strong>
              </p>

              <div className="space-y-4 mb-10">
                {[
                  { name: 'Dolby IMS3000', desc: 'Integración directa vía FTP' },
                  { name: 'Barco SPXK & ICMP', desc: 'Compatibilidad nativa certificada' },
                  { name: 'Alchemy GDC', desc: 'Transferencia segura y optimizada' }
                ].map((server) => (
                  <div key={server.name} className="flex items-center gap-4 bg-white/5 border border-white/10 rounded-lg p-4 hover:border-[#e4002b]/30 transition-colors group">
                    <div className="w-10 h-10 bg-[#e4002b]/20 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-[#e4002b]/30 transition-colors">
                      <Check className="w-5 h-5 text-[#e4002b]" />
                    </div>
                    <div>
                      <h4 className="text-white font-bold">{server.name}</h4>
                      <p className="text-white/50 text-sm">{server.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* =============== SUMMARY / CTA =============== */}
      <section className="relative py-24 md:py-32 overflow-hidden" data-testid="dcp-summary">
        <div className="absolute inset-0">
          <img src={CINEMA_DARK} alt="Sala de cine" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/80 to-[#0a0a0a]" />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <AnimatedSection className="max-w-3xl mx-auto text-center">
            <p className="text-[#e4002b] text-sm font-bold tracking-widest uppercase mb-4">En resumen</p>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-8" style={{ fontFamily: '"Oswald", sans-serif' }}>
              MENOS PREOCUPACIONES,<br />MÁS TIEMPO PARA TU NEGOCIO
            </h2>
            <p className="text-xl text-white/70 leading-relaxed mb-10">
              Bravo elimina los intermediarios físicos y los procesos manuales complicados para ofrecerte
              un modelo de gestión mucho más <strong className="text-white">cómodo, ágil y moderno</strong>.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-5">
              <a href="mailto:m.cazorla@versiondigital.es?subject=Solicitud Demo Bravo DCP"
                className="inline-flex items-center gap-3 bg-[#e4002b] text-white px-10 py-4 font-bold tracking-wider uppercase hover:bg-[#c70024] transition-all hover:scale-105 shadow-[0_0_40px_rgba(228,0,43,0.3)]"
                data-testid="dcp-cta-demo-btn">
                Solicitar Demo <ArrowRight className="w-5 h-5" />
              </a>
              <a href="mailto:m.cazorla@versiondigital.es?subject=Contratar Bravo DCP"
                className="inline-flex items-center gap-3 border-2 border-white text-white px-10 py-4 font-bold tracking-wider uppercase hover:bg-white hover:text-[#0a0a0a] transition-all"
                data-testid="dcp-cta-contract-btn">
                Contratar <Mail className="w-5 h-5" />
              </a>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <Footer />
      <AuthModal isOpen={showAuth} onClose={() => setShowAuth(false)} />
      <SearchModal isOpen={showSearch} onClose={() => setShowSearch(false)} />
    </div>
  );
};

export default DistribucionDCPPage;
